import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { fetchShopDebt, type ShopFundingDebt } from '../services/api';
import { MONEY } from '@kioskgaming/ui';

/**
 * Persistent freeze banner (documents/SHOP_FUNDING_DEBT.md): shown app-wide
 * while the shop has outstanding funding debt. Explains what is blocked and
 * exactly how to settle (top up = automatic repayment). Re-checks on route
 * change and window focus so it disappears promptly after settlement.
 */
export default function ShopDebtFreezeBanner() {
  const [debt, setDebt] = useState<ShopFundingDebt | null>(null);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchShopDebt()
        .then((res) => {
          if (!cancelled && res.success && res.data) setDebt(res.data);
        })
        .catch(() => {
          /* banner is best-effort */
        });
    };
    load();
    window.addEventListener('focus', load);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', load);
    };
  }, [location.pathname]);

  if (!debt?.frozen) return null;

  return (
    <div role="alert" className="border-b border-rose-200 bg-rose-50 px-4 py-3">
      <div className="mx-auto flex w-full max-w-none flex-wrap items-center gap-3 px-4 sm:px-6 lg:px-10">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
          <AlertTriangle size={16} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-rose-800">
            Outstanding funding debt: {debt.outstanding_credits.toLocaleString('en-US', { minimumFractionDigits: 2 })} credits
            — Shop operations are paused
          </p>
          <p className="text-xs text-rose-700">
            Counter deposits, redemptions and withdrawals are unavailable, and your Players cannot withdraw
            {debt.online_deposits_blocked ? '; new online deposits are also paused' : ''}.
            Buy credits to settle — debt is repaid automatically from incoming credits.
          </p>
        </div>
        <Link
          to="/?openCreditPurchase=1"
          state={{ openCreditPurchase: true }}
          className="shrink-0 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-rose-700"
        >
          {MONEY.buyCredits} now
        </Link>
      </div>
    </div>
  );
}
